import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

import { useAuth } from '@/contexts/AuthContext';
import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import { useWorkspaceStore } from '@/stores/workspace-store';
import type {
  UserWorkspaceResponse,
  PendingInvitationPreview,
  CurrentWorkspaceInfo,
} from '@optidev/shared-types';

interface WorkspaceContextValue {
  /** Currently active workspace ID (from URL or default) */
  activeWorkspaceId: string | null;
  /** Currently active workspace details */
  activeWorkspace: UserWorkspaceResponse | null;
  /** All workspaces user belongs to */
  workspaces: UserWorkspaceResponse[];
  /** Non-member workspace being viewed (platform admin only) */
  currentWorkspace: CurrentWorkspaceInfo | null;
  /** Pending invitations for user */
  pendingInvitations: PendingInvitationPreview[];
  /** Number of active workspaces the user owns */
  ownedWorkspaceCount: number;
  /** Loading state */
  loading: boolean;
  /** Switch to a different workspace (updates URL) */
  switchWorkspace: (workspaceId: string) => void;
  /** Refresh workspaces list */
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

/**
 * Workspace Context Provider
 *
 * Manages workspace state via Zustand store
 * - Store is source of truth for active workspace (initialized from URL on load)
 * - URL (?ws=xxx) synced for bookmarking/reload convenience
 * - No server call needed for switching (instant)
 */
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [workspaces, setWorkspaces] = useState<UserWorkspaceResponse[]>([]);
  const [currentWorkspace, setCurrentWorkspace] =
    useState<CurrentWorkspaceInfo | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<
    PendingInvitationPreview[]
  >([]);
  const [ownedWorkspaceCount, setOwnedWorkspaceCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Use store for active workspace (store initializes from URL automatically)
  const { activeWorkspaceId: storeWorkspaceId, setActiveWorkspaceId } =
    useWorkspaceStore();
  const defaultWorkspaceId = user?.workspaceId || null;
  const activeWorkspaceId = storeWorkspaceId || defaultWorkspaceId;

  // Set default workspace in store if not set from URL
  useEffect(() => {
    if (!storeWorkspaceId && defaultWorkspaceId) {
      useWorkspaceStore.setState({ activeWorkspaceId: defaultWorkspaceId });
    }
  }, [storeWorkspaceId, defaultWorkspaceId]);

  // Find active workspace details (defensive: workspaces might be undefined from API)
  const activeWorkspace =
    (workspaces || []).find((w) => w.id === activeWorkspaceId) || null;

  // Fetch user's workspaces
  const refreshWorkspaces = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // X-Workspace-Id header is added by axios interceptor for platform admin support
      const data = await workspaceMembersApi.getMyWorkspaces();
      setWorkspaces(data.workspaces || []);
      setCurrentWorkspace(data.currentWorkspace || null);
      setPendingInvitations(data.pendingInvitations || []);
      setOwnedWorkspaceCount(data.ownedWorkspaceCount ?? 0);
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);

      // Handle workspace access errors (invalid workspace ID in URL)
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        const errorData = error.response.data;
        const message = errorData?.message || 'Unable to access workspace';

        toast.error(message);

        // Clear invalid workspace and redirect to default
        // Setting to null will fall back to user's default workspace
        setActiveWorkspaceId(null);
      }
    } finally {
      setLoading(false);
    }
  }, [user, activeWorkspaceId]);

  // Load workspaces on mount
  useEffect(() => {
    if (user) {
      refreshWorkspaces();
    }
  }, [user, refreshWorkspaces]);

  // Fall back to the first available workspace when the resolved active workspace
  // isn't in the loaded (scope-filtered) list — e.g. an embed-first user on
  // optidev.ai whose default (user.workspaceId) still points at their hidden embed
  // workspace. Keeps the UI and the X-Workspace-Id header in agreement. Skipped
  // while loading and when a platform admin is viewing a non-member workspace
  // (currentWorkspace holds that context).
  useEffect(() => {
    if (loading || currentWorkspace) return;
    if (workspaces.length === 0) return;
    const activeInList = workspaces.some((w) => w.id === activeWorkspaceId);
    if (!activeInList) {
      setActiveWorkspaceId(workspaces[0].id);
    }
  }, [
    loading,
    currentWorkspace,
    workspaces,
    activeWorkspaceId,
    setActiveWorkspaceId,
  ]);

  // Switch workspace (update store, URL synced automatically)
  const switchWorkspace = useCallback(
    (workspaceId: string) => {
      setActiveWorkspaceId(workspaceId);
    },
    [setActiveWorkspaceId],
  );

  // Auto-switch to new workspace after Stripe Checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new_workspace') === 'created' && workspaces.length > 0) {
      const ownedWorkspaces = workspaces.filter((w) => w.isOwner);
      const newest = ownedWorkspaces[ownedWorkspaces.length - 1];
      if (newest && newest.id !== activeWorkspaceId) {
        switchWorkspace(newest.id);
        toast.success(`Switched to "${newest.name}"`);
      }
      // Clean up URL param — re-read from window.location since switchWorkspace()
      // already updated it via replaceState (added ?ws=newId)
      const freshParams = new URLSearchParams(window.location.search);
      freshParams.delete('new_workspace');
      const newUrl = freshParams.toString()
        ? `${window.location.pathname}?${freshParams.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [workspaces, activeWorkspaceId, switchWorkspace]);

  return (
    <WorkspaceContext.Provider
      value={{
        activeWorkspaceId,
        activeWorkspace,
        workspaces: workspaces || [],
        currentWorkspace,
        pendingInvitations: pendingInvitations || [],
        ownedWorkspaceCount,
        loading,
        switchWorkspace,
        refreshWorkspaces,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

/**
 * Hook to access workspace context
 */
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

/**
 * Hook to get just the active workspace ID (for API calls)
 */
export function useActiveWorkspaceId(): string | null {
  const { activeWorkspaceId } = useWorkspace();
  return activeWorkspaceId;
}
